import { useCallback, useEffect, useMemo } from "react";
import styles from "./ConnectSplash.module.css";
import { usePreviewStores } from "../../data/CmsPreviewContext";
import PushButton from "../../UI/Buttons/PushButton/PushButton";
import logo from "../../assets/media/logo-with-tagline.png";
import { lockBodyScroll, unlockBodyScroll } from "../../Hooks/bodyScrollLock";
import { resolveConnectSplash } from "../../data/connectSplashConfig";
import { linkifyEmails } from "../../data/linkifyEmails";

const buttonStyles = {
  margin: 0,
  minWidth: "9em",
  borderRadius: "50px",
  letterSpacing: "0.1em",
  textTransform: "uppercase",
  fontSize: "1.35rem",
  padding: "0.55em 1.25em",
};

/**
 * Full-viewport connect splash.
 * pending; Kit accepted the signup; they still need to confirm by email.
 * confirmed; they clicked the confirmation link; then continue into the site.
 */
const ConnectSplash = ({ variant = "pending", email, onDismiss }) => {
  const { content, isPreview } = usePreviewStores();
  const resolved = useMemo(() => resolveConnectSplash(content), [content]);
  const copy = variant === "confirmed" ? resolved.confirmed : resolved.pending;
  const isConfirmed = variant === "confirmed";
  const continueId = "connect-splash-continue";
  const autoSeconds = isPreview ? 0 : resolved.confirmed.autoSeconds;
  const showHelp = resolved.showHelpLine && !!resolved.helpLine;

  const dismiss = useCallback(() => {
    if (typeof onDismiss === "function") onDismiss();
  }, [onDismiss]);

  useEffect(() => {
    lockBodyScroll();
    const focusId = window.requestAnimationFrame(() => {
      document.getElementById(continueId)?.focus();
    });

    const onKeyDown = (e) => {
      if (e.key === "Escape") dismiss();
    };
    document.addEventListener("keydown", onKeyDown);

    return () => {
      window.cancelAnimationFrame(focusId);
      document.removeEventListener("keydown", onKeyDown);
      unlockBodyScroll();
    };
  }, [dismiss]);

  useEffect(() => {
    if (!isConfirmed) return;
    if (!autoSeconds) return;
    const reduce =
      typeof window !== "undefined" &&
      window.matchMedia("(prefers-reduced-motion: reduce)").matches;
    if (reduce) return;
    const id = window.setTimeout(dismiss, autoSeconds * 1000);
    return () => window.clearTimeout(id);
  }, [isConfirmed, autoSeconds, dismiss]);

  return (
    <div
      className={styles.modal}
      role="dialog"
      aria-modal="true"
      aria-labelledby="connect-splash-title"
      aria-describedby={
        showHelp ? "connect-splash-lead connect-splash-help" : "connect-splash-lead"
      }
      data-connect-splash={variant}
    >
      <div className={styles.panel}>
        <div className={styles["panel-glow"]} aria-hidden="true" />
        <div className={styles.mark} aria-hidden="true">
          <img src={logo} alt="" width="2000" height="902" />
        </div>
        <div className={styles.content}>
          <p className={styles.eyebrow}>{copy.eyebrow}</p>
          <h1 id="connect-splash-title" className={styles.title}>
            {copy.title}
          </h1>
          <p id="connect-splash-lead" className={styles.lead}>
            {copy.lead}
          </p>
          {variant === "pending" && copy.showSentEmail && email && (
            <p className={styles.sent}>
              {copy.sentPrefix} <span className={styles.email}>{email}</span>
            </p>
          )}
          {variant === "pending" && copy.showNote && (
            <p className={styles.note}>
              <span className={styles["note-label"]}>{copy.noteLabel}</span>
              {copy.note}
            </p>
          )}
          {isConfirmed && copy.continueLine && autoSeconds > 0 && (
            <p className={styles.continue}>{copy.continueLine}</p>
          )}
        </div>
        {showHelp && (
          <p id="connect-splash-help" className={styles.help}>
            {linkifyEmails(resolved.helpLine)}
          </p>
        )}
        <div className={styles.actions}>
          <PushButton
            inputOrButton="button"
            colorType="primary"
            size="body"
            identifier={continueId}
            onClick={dismiss}
            styles={buttonStyles}
          >
            {copy.button}
          </PushButton>
        </div>
      </div>
    </div>
  );
};

export default ConnectSplash;
